import { useEffect } from 'react'
import type { EditorUndoRedo } from '../client/editor-registry.js'

/**
 * Claim Ctrl+Z for an editor that cannot answer it.
 *
 * Ctrl+Z is routed by REGION (see UndoProvider): the pane the user is in
 * decides. Inside the editor region, an editor that can be edited owns the
 * keystroke even when its own history is empty — "nothing to undo right now"
 * is not "not my keystroke". An editor that registers NOTHING is read as a
 * viewer, and the keystroke falls through to the drive's file-operation
 * stack: the user asks to undo what they just did and a file deleted minutes
 * ago reappears instead, somewhere else in the app entirely.
 *
 * This hook fills that gap for a surface the user CAN change but whose
 * changes nothing records. It registers an editor that does nothing and
 * reports nothing to undo, so the keystroke stops here and the Edit menu
 * greys out — honest about having no undo without lying about who owns the
 * pane.
 *
 * `hasUnrecordedChanges` is the claim. Pass a real flag where the surface is
 * only sometimes changeable (OnlyOffice passes `dirty`, so an untouched
 * document leaves the drive's undo alone), or a constant `true` where the
 * pane is always the user's. Pass `undefined` for `registerUndoRedo`
 * wherever something else is already registering — the two share one slot
 * and the last one to register wins.
 *
 * NO TIMESTAMP, deliberately, and none is needed: the region already says
 * who owns the keystroke, so there is no comparison to date. An earlier
 * design did rank by recency and could not be made honest here — the DocsAPI
 * office editor announces "dirty" once and then says nothing for the rest of
 * that period, so a stamp taken at the first keystroke would let a file
 * deleted ten minutes into writing win and be restored instead.
 *
 * The global listener stands aside for inputs, textareas and contenteditable,
 * so a surface with its own native undo keeps it while the caret is in there;
 * this covers the rest of the pane — toolbars, panels, the chrome around an
 * embedded office editor.
 *
 * This is a stopgap per editor. Drop the call the moment that editor
 * registers real undo/redo.
 */
export function useUndoScopeGuard(
  registerUndoRedo: ((editor: EditorUndoRedo) => () => void) | undefined,
  hasUnrecordedChanges: boolean,
): void {
  // RE-REGISTER when the claim flips, rather than hiding it behind a getter.
  //
  // The getter version registered once and let UndoProvider read the current
  // value at resolve time, which kept the KEYSTROKE correct — but the Edit
  // menu only followed because this hook re-published a fresh object on every
  // flip, and the provider re-rendered because that object was a new
  // identity, never because its contents changed (they are always
  // `{false, false}`). Anything reasonable — hoisting that literal to a
  // constant, memoising it, adding a shallow-equality bail-out to
  // `setEditorState` — would have left the menu silently stuck while the
  // keystroke moved, which is the one thing UndoProvider says must never
  // happen. Re-registering makes the dependency a real one: the same cheap
  // swap a tab switch already performs.
  useEffect(() => {
    if (!registerUndoRedo) return
    return registerUndoRedo({
      undo: async () => false,
      redo: async () => false,
      editable: hasUnrecordedChanges,
      onStateChange: (cb) => {
        cb({ canUndo: false, canRedo: false })
        return () => {}
      },
    })
  }, [registerUndoRedo, hasUnrecordedChanges])
}
